import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Pencil, UploadCloud, X, Plus, RotateCcw, UserPlus, KeyRound, AtSign, Lock } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import { NewEmployeeDialog, ResetPasswordDialog } from "@/components/EmployeeAdminDialogs";
import UpdateLoginDialog from "@/components/UpdateLoginDialog";

// Convert relative avatar path to absolute URL so <img> works from any host
// (kept for backward compat inside this file; the shared Avatar component uses its own copy)
function absAvatar(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${API_BASE}${url.slice(4)}`; // /api/... -> ${BACKEND}/api/...
  return url;
}

export default function Employees() {
  const [emps, setEmps] = useState([]);
  const [specs, setSpecs] = useState({});
  const [open, setOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [loginFor, setLoginFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", active: true, avatar: "", skills: [], allowed_specializations: [], allowed_menus: [] });
  const [skillDraft, setSkillDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [menuCatalog, setMenuCatalog] = useState({ all: {}, toggleable: [], always_on: [] });
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/employees");
    setEmps(data);
  };
  useEffect(() => {
    load();
    api.get("/catalog/specializations").then(r => setSpecs(r.data)).catch(() => {});
    api.get("/catalog/menus").then(r => setMenuCatalog(r.data)).catch(() => {});
  }, []);

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name || "", email: e.email || "", phone: e.phone || "",
      active: e.active !== false, avatar: e.avatar || "", skills: [...(e.skills || [])],
      allowed_specializations: [...(e.allowed_specializations || [e.specialization])],
      allowed_menus: [...(e.allowed_menus || [])],
    });
    setSkillDraft("");
    setOpen(true);
  };

  const toggleAllowed = (k) => setForm(f => ({
    ...f, allowed_specializations: f.allowed_specializations.includes(k)
      ? f.allowed_specializations.filter(x => x !== k)
      : [...f.allowed_specializations, k],
  }));

  const toggleMenu = (k) => setForm(f => ({
    ...f, allowed_menus: f.allowed_menus.includes(k)
      ? f.allowed_menus.filter(x => x !== k)
      : [...f.allowed_menus, k],
  }));

  const save = async () => {
    if (!editing) return;
    const name = (form.name || "").trim();
    const email = (form.email || "").trim();
    const phone = (form.phone || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.patch(`/employees/${editing.id}`, {
        name, email, phone, active: form.active,
        avatar: form.avatar,
        skills: form.skills,
        allowed_specializations: form.allowed_specializations,
        allowed_menus: form.allowed_menus,
      });
      toast.success("Employee updated", { description: `${name} · ${editing.title}` });
      setOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Update failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const uploadAvatar = async (file) => {
    if (!editing || !file) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/employees/${editing.id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, avatar: data.avatar }));
      toast.success("Avatar uploaded");
      await load();
    } catch (e) {
      toast.error("Upload failed", { description: e.response?.data?.detail || e.message });
    } finally { setAvatarBusy(false); }
  };

  const resetToDefaults = () => {
    if (!editing) return;
    const defaults = editing.skills_default || [];
    setForm((f) => ({ ...f, skills: [...defaults] }));
    if (defaults.length) toast("Restored role defaults", { description: `${defaults.length} skill(s)` });
    else toast("No defaults for this role");
  };

  const addSkill = () => {
    const v = skillDraft.trim();
    if (!v) return;
    if (form.skills.includes(v)) { setSkillDraft(""); return; }
    setForm((f) => ({ ...f, skills: [...f.skills, v] }));
    setSkillDraft("");
  };
  const removeSkill = (s) => setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }));

  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Team · Specialization-first assignment</div>
            <h1 className="font-heading font-black tracking-tight text-4xl mt-2">Employees</h1>
            <p className="text-sm text-zinc-600 mt-2 max-w-xl">
              Tasks are auto-assigned by service → specialization. Load is balanced by picking the least-loaded specialist.
            </p>
          </div>
          <Button data-testid="new-employee-btn" onClick={() => setNewOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
            <UserPlus size={16} className="mr-1" /> New Employee
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {emps.map(e => (
          <article key={e.id} data-testid={`employee-card-${e.id}`} className={`bg-white border p-5 relative ${e.active === false ? "opacity-60 border-zinc-300" : "border-zinc-200"}`}>
            <button
              data-testid={`edit-employee-${e.id}`}
              onClick={() => openEdit(e)}
              title="Edit details"
              className="absolute top-3 right-3 p-1.5 hover:bg-zinc-100 rounded transition-colors"
            >
              <Pencil size={15} />
            </button>
            <button
              data-testid={`reset-employee-${e.id}`}
              onClick={() => setResetFor(e)}
              title="Reset password"
              className="absolute top-3 right-10 p-1.5 hover:bg-amber-50 rounded transition-colors"
            >
              <KeyRound size={15} className="text-amber-700" />
            </button>
            <button
              data-testid={`update-login-${e.id}`}
              onClick={() => setLoginFor(e)}
              title="Update login id / password"
              className="absolute top-3 right-[68px] p-1.5 hover:bg-sky-50 rounded transition-colors"
            >
              <AtSign size={15} className="text-sky-700" />
            </button>
            <div className="flex items-center gap-4">
              <Avatar src={e.avatar} name={e.name} size="lg" testid={`avatar-${e.id}`} />
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-lg tracking-tight truncate" data-testid={`employee-name-${e.id}`}>{e.name}</div>
                <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold">{e.title}</div>
                <div className="text-xs text-zinc-500 truncate" data-testid={`employee-email-${e.id}`}>{e.email}</div>
                {e.login_email && e.login_email !== e.email ? (
                  <div className="text-[10px] text-amber-700 truncate" data-testid={`employee-login-${e.id}`} title="Actual login email">
                    Login: <span className="font-mono">{e.login_email}</span>
                  </div>
                ) : null}
                {e.is_locked ? (
                  <div className="text-[10px] text-rose-700 flex items-center gap-1 mt-0.5" data-testid={`employee-locked-${e.id}`}>
                    <Lock size={10} /> Locked · click <AtSign size={10} /> to unlock
                  </div>
                ) : null}
                {e.phone ? <div className="text-xs text-zinc-500 truncate tabular" data-testid={`employee-phone-${e.id}`}>{e.phone}</div> : null}
              </div>
            </div>
            {e.active === false ? (
              <div className="mt-3 text-[10px] tracking-[0.14em] uppercase font-semibold text-rose-700 bg-rose-50 border border-rose-200 inline-block px-2 py-0.5">
                Inactive · will not receive new tasks
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Kpi label="Open" value={e.open_tasks || 0} />
              <Kpi label="Overdue" value={e.overdue_tasks || 0} accent={e.overdue_tasks ? "danger" : "default"} />
            </div>
            <div className="mt-4">
              <div className="text-[10px] tracking-[0.16em] uppercase text-zinc-500 font-semibold">Auto-assignment categories</div>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`allowed-cats-${e.id}`}>
                {(e.allowed_specializations || [e.specialization]).map(k => (
                  <span key={k} className="text-[10px] px-1.5 py-0.5 bg-zinc-900 text-white uppercase tracking-wider font-semibold">
                    {specs[k]?.title || k}
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[10px] tracking-[0.16em] uppercase text-zinc-500 font-semibold flex items-center justify-between">
                <span>Menus visible</span>
                <span className="tabular text-zinc-400 font-normal">{(e.allowed_menus || []).filter(m => (menuCatalog.toggleable || []).includes(m)).length}/{(menuCatalog.toggleable || []).length}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`allowed-menus-${e.id}`}>
                {(e.allowed_menus || []).filter(m => (menuCatalog.toggleable || []).includes(m)).length === 0 ? (
                  <span className="text-[10px] text-zinc-400 italic">Base menus only (Overview · Tasks · Profile)</span>
                ) : (
                  (e.allowed_menus || [])
                    .filter(m => (menuCatalog.toggleable || []).includes(m))
                    .map(m => (
                      <span key={m} className="text-[10px] px-1.5 py-0.5 bg-sky-50 border border-sky-200 text-sky-800 uppercase tracking-wider font-semibold">
                        {menuCatalog.all?.[m] || m}
                      </span>
                    ))
                )}
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[10px] tracking-[0.16em] uppercase text-zinc-500 font-semibold">Specialisation covers</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {e.skills.slice(0, 8).map(s => (
                  <span key={s} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 border border-zinc-200">{s}</span>
                ))}
                {e.skills.length > 8 && <span className="text-[10px] text-zinc-500 px-1.5 py-0.5">+{e.skills.length - 8} more</span>}
              </div>
            </div>
          </article>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-2xl">Edit Employee</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Update photo, contact details and the skills the specialist actually covers. Specialization role is fixed.</DialogDescription>
          </DialogHeader>
          {editing ? (
            <div className="space-y-5">
              {/* Avatar */}
              <div className="flex items-start gap-4 pb-4 border-b border-zinc-200">
                <div className="relative">
                  <Avatar src={form.avatar || editing.avatar} name={form.name || editing.name} size="xl" />
                  {avatarBusy ? (
                    <div className="absolute inset-0 bg-black/50 rounded-full grid place-items-center text-white text-[10px] font-semibold">…</div>
                  ) : null}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="text-[10px] tracking-[0.16em] uppercase text-zinc-500 font-semibold">Photo</div>
                  <input
                    ref={fileRef}
                    data-testid="employee-avatar-input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={(ev) => uploadAvatar(ev.target.files?.[0])}
                  />
                  <div className="flex gap-2">
                    <Button
                      data-testid="employee-avatar-upload-btn"
                      type="button" variant="outline"
                      onClick={() => fileRef.current?.click()}
                      disabled={avatarBusy}
                      className="rounded-none border-zinc-300"
                    >
                      <UploadCloud size={14} className="mr-1.5" /> Upload image
                    </Button>
                    <Input
                      data-testid="employee-avatar-url-input"
                      value={form.avatar}
                      onChange={(ev) => setForm({ ...form, avatar: ev.target.value })}
                      placeholder="or paste an image URL"
                      className="flex-1"
                    />
                  </div>
                  <div className="text-[10px] text-zinc-500">PNG · JPG · WEBP · GIF · max 5 MB. Uploads stored securely; paste any public URL as an alternative.</div>
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Full Name</Label>
                  <Input data-testid="employee-name-input" value={form.name} onChange={(ev) => setForm({ ...form, name: ev.target.value })} />
                </div>
                <div>
                  <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Email</Label>
                  <Input data-testid="employee-email-input" type="email" value={form.email} onChange={(ev) => setForm({ ...form, email: ev.target.value })} />
                </div>
                <div>
                  <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Phone</Label>
                  <Input data-testid="employee-phone-input" value={form.phone} onChange={(ev) => setForm({ ...form, phone: ev.target.value })} />
                </div>
              </div>

              {/* Skills editor */}
              <div className="border-t border-zinc-200 pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Specialisation covers</Label>
                  <button
                    data-testid="employee-skills-reset-btn"
                    onClick={resetToDefaults}
                    className="text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
                  >
                    <RotateCcw size={11} /> Reset to role defaults
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 min-h-[36px] p-2 border border-zinc-200 bg-zinc-50" data-testid="employee-skills-chips">
                  {form.skills.length === 0 ? (
                    <span className="text-xs text-zinc-500 italic">No custom skills — will fall back to role defaults on save.</span>
                  ) : form.skills.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-white border border-zinc-300">
                      {s}
                      <button
                        data-testid={`remove-skill-${s}`}
                        onClick={() => removeSkill(s)}
                        className="hover:text-rose-600"
                        title="Remove"
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <Input
                    data-testid="employee-skill-input"
                    value={skillDraft}
                    onChange={(ev) => setSkillDraft(ev.target.value)}
                    onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); addSkill(); } }}
                    placeholder="Add a skill and press Enter (e.g. GSTR-3B filing)"
                  />
                  <Button
                    data-testid="employee-skill-add-btn"
                    type="button" variant="outline"
                    onClick={addSkill}
                    className="rounded-none border-zinc-300"
                  >
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
              </div>

              {/* Allowed specializations */}
              <div className="border-t border-zinc-200 pt-4">
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Dashboard access · task categories</Label>
                <div className="mt-2 grid grid-cols-2 gap-1.5 border border-zinc-200 p-2 bg-zinc-50 max-h-40 overflow-y-auto" data-testid="allowed-specs">
                  {Object.keys(specs).map(k => (
                    <label key={k} className="flex items-center gap-2 text-xs px-1 py-0.5 hover:bg-white cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`allowed-spec-${k}`}
                        checked={form.allowed_specializations.includes(k)}
                        onChange={() => toggleAllowed(k)}
                      />
                      <span>{specs[k]?.title}</span>
                    </label>
                  ))}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">Auto-assignment considers this employee only for the ticked categories.</div>
              </div>

              {/* Menu access — which sidebar sections this employee sees */}
              <div className="border-t border-zinc-200 pt-4">
                <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Dashboard menus visible to this employee</Label>
                <div className="mt-2 grid grid-cols-2 gap-1.5 border border-zinc-200 p-2 bg-zinc-50" data-testid="allowed-menus">
                  {(menuCatalog.toggleable || []).map(k => (
                    <label key={k} className="flex items-center gap-2 text-xs px-1 py-0.5 hover:bg-white cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`allowed-menu-${k}`}
                        checked={form.allowed_menus.includes(k)}
                        onChange={() => toggleMenu(k)}
                      />
                      <span>{menuCatalog.all?.[k]}</span>
                    </label>
                  ))}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Overview, Daily Tasks and My Profile are always shown. Tick extras to expose Invoices, Clients, Leads or Reports on their sidebar.
                </div>
              </div>

              {/* Active */}
              <div className="flex items-center justify-between border border-zinc-200 p-3">
                <div>
                  <div className="text-sm font-semibold">Active</div>
                  <div className="text-[11px] text-zinc-500">Inactive employees won&apos;t be auto-assigned new tasks.</div>
                </div>
                <Switch
                  data-testid="employee-active-switch"
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="employee-save-btn" onClick={save} disabled={busy || !form.name?.trim()} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NewEmployeeDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
      <ResetPasswordDialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)} employee={resetFor || {}} />
      <UpdateLoginDialog open={!!loginFor} onOpenChange={(v) => !v && setLoginFor(null)} employee={loginFor || {}} onSaved={load} />
    </div>
  );
}

function Kpi({ label, value, accent = "default" }) {
  const color = { default: "text-zinc-900", danger: "text-rose-700" }[accent];
  return (
    <div className="border border-zinc-200 p-3">
      <div className="text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-500">{label}</div>
      <div className={`font-heading font-bold text-2xl tabular mt-1 ${color}`}>{value}</div>
    </div>
  );
}
